/**
 * TaskCommentCard - Renders a task comment forwarded into team chat
 *
 * Displays author badge (client/partner/staff), task title, project name,
 * and the comment content. Styled similarly to WhatsAppForwardCard.
 */

import React from 'react';
import { ClipboardList, User, Handshake, UserCog } from 'lucide-react';

interface TaskCommentData {
  task_id: string;
  task_title: string;
  task_status?: string;
  project_id: string;
  project_name: string;
  comment_content: string;
  author_name: string;
  author_type: 'client' | 'partner' | 'staff';
}

const authorConfig: Record<string, { label: string; color: string; bg: string; Icon: React.FC<any> }> = {
  client: { label: 'Cliente', color: '#EA580C', bg: 'rgba(234,88,12,0.08)', Icon: User },
  partner: { label: 'Partner', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', Icon: Handshake },
  staff: { label: 'Staff', color: '#2383E2', bg: 'rgba(35,131,226,0.08)', Icon: UserCog },
};

const statusLabels: Record<string, string> = {
  todo: 'Da fare',
  in_progress: 'In corso',
  completed: 'Completata',
  review: 'In revisione',
  blocked: 'Bloccata',
};

export const TaskCommentCard: React.FC<{ content: string }> = ({ content }) => {
  let data: TaskCommentData | null = null;
  try {
    const obj = JSON.parse(content);
    if (obj && obj.task_id && obj.comment_content) data = obj;
  } catch { /* fallback below */ }

  if (!data) {
    return (
      <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#37352F' }}>
        {content}
      </div>
    );
  }

  const config = authorConfig[data.author_type] || authorConfig.client;
  const { Icon } = config;

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(35,131,226,0.25)', backgroundColor: 'rgba(35,131,226,0.04)' }}>
      {/* Header */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium" style={{ backgroundColor: 'rgba(35,131,226,0.10)', color: '#2383E2' }}>
        <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
        Commento su task
      </div>

      <div className="px-3 py-2 space-y-1.5">
        {/* Author badge + name */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ color: config.color, backgroundColor: config.bg }}
          >
            <Icon className="w-3 h-3" strokeWidth={1.5} />
            {config.label}
          </span>
          <span className="text-[11px] font-medium" style={{ color: '#37352F' }}>{data.author_name}</span>
        </div>

        {/* Task title */}
        <div className="text-[13px] font-semibold leading-snug" style={{ color: '#37352F' }}>
          {data.task_title}
          {data.task_status && (
            <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#787774' }}>
              {statusLabels[data.task_status] || data.task_status}
            </span>
          )}
        </div>

        {/* Project name */}
        <div className="text-[11px]" style={{ color: '#787774' }}>
          {data.project_name}
        </div>

        {/* Comment content */}
        <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: '#37352F' }}>
          {data.comment_content}
        </div>
      </div>
    </div>
  );
};

export default TaskCommentCard;
